import {
  InfiniteData,
  keepPreviousData,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from 'react-query-v5';
import api from '../../api';
import {
  CreateTicketCommentExternalDto,
  CreateTicketCommentResponseDto,
  CreateTicketDto,
  TicketListDto,
  TicketWithDetailsDto,
} from '../../api-client';
import { QueryKey } from '../../enums';
import {
  InferSearchParamsQueryTypeWithArraySorter,
  PaginationOptionsWithSortArray,
} from '../../types';

const CACHE_TIME = 5000;

export const useOrganizationTicketsList = (
  organizationId: string,
  query: PaginationOptionsWithSortArray & { searchTerm?: string },
  options?: UseQueryOptions<TicketListDto>
) =>
  useQuery({
    ...options,
    enabled: !!organizationId,
    queryKey: [QueryKey.Resource.OrganizationTickets, organizationId, query],
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    queryFn: async () =>
      api.organization.organizationTicketsControllerGetTickets(
        organizationId,
        query
      ),
  });

export const useInfiniteOrganizationTicketList = (
  payload: InferSearchParamsQueryTypeWithArraySorter<
    typeof api.organization.organizationTicketsControllerGetTickets,
    1
  >,
  organizationId?: string,
  options?: Partial<
    UseInfiniteQueryOptions<TicketListDto, Error, InfiniteData<TicketListDto>>
  >
) => {
  return useInfiniteQuery({
    ...options,
    enabled: !!organizationId,
    queryKey: [
      QueryKey.Resource.OrganizationTickets,
      organizationId,
      QueryKey.Operation.List,
      payload,
      QueryKey.Operation.Infinite,
    ],

    queryFn: async ({ pageParam }) => {
      const data =
        await api.organization.organizationTicketsControllerGetTickets(
          organizationId || '',
          {
            ...payload,
            page: pageParam as number,
          }
        );

      return { ...data, newItems: 0 };
    },

    getNextPageParam: lastPage => {
      const hasNextPage = lastPage.hasNextPage;
      const nextPage = lastPage.page + 1;

      return hasNextPage ? nextPage : undefined;
    },
    placeholderData: keepPreviousData,
    initialPageParam: payload?.page || 0,
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
  });
};

export const useCreateOrganizationTicket = (
  organizationId: string,
  options?: UseMutationOptions<TicketWithDetailsDto, Error, CreateTicketDto>,
  isExtension?: boolean
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,

    mutationFn: async data =>
      api.organization.organizationTicketsControllerCreateTicket(
        organizationId,
        data
      ),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.OrganizationTickets, organizationId],
      });
      if (isExtension) {
        await queryClient.invalidateQueries({
          queryKey: [QueryKey.Resource.Tickets, QueryKey.Operation.List],
        });

        await queryClient.invalidateQueries({
          queryKey: [QueryKey.Resource.TicketOrganizationGroup],
        });

        await queryClient.invalidateQueries({
          queryKey: [
            QueryKey.Resource.TicketAssigneeGroup,
            QueryKey.Operation.List,
          ],
        });
        await queryClient.invalidateQueries({
          queryKey: [QueryKey.Resource.TicketTypeGroup],
        });
      }
      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const useGetOneOrganizationTicket = (
  ticketId: string,
  organizaitonId: string,
  options?: Partial<
    UseQueryOptions<TicketWithDetailsDto, Error, TicketWithDetailsDto>
  >
) => {
  return useQuery({
    ...options,
    enabled: !!ticketId || !!organizaitonId,
    queryKey: [QueryKey.Resource.OrganizationTickets, organizaitonId, ticketId],
    queryFn: async () =>
      api.organization.organizationTicketsControllerGetTicket(
        ticketId,
        organizaitonId
      ),
    placeholderData: keepPreviousData,
  });
};

export const useCreateOrganizationTicketComment = (
  organizationId: string,
  ticketId: string,
  options?: UseMutationOptions<
    CreateTicketCommentResponseDto,
    Error,
    CreateTicketCommentExternalDto
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,

    mutationFn: async data =>
      await api.organization.organizationTicketsControllerCreateExternalComment(
        ticketId,
        organizationId,
        data
      ),

    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({
        queryKey: [
          QueryKey.Resource.OrganizationTickets,
          organizationId,
          ticketId,
        ],
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
};
